/**
 * Zod contract for the seller's own commission-fee status.
 * Powers the fee explainer on the sell page and above my-listings.
 */

import { z } from 'zod';

export const SellerFeeProgressSchema = z.object({
  /** Human sentence, e.g. "Active listings: at least 100". */
  label: z.string(),
  /** Where the seller is now against that threshold. */
  current: z.number(),
  target: z.number(),
  met: z.boolean(),
});

export type SellerFeeProgress = z.infer<typeof SellerFeeProgressSchema>;

export const SellerFeeStatusSchema = z.object({
  /** The rate this seller actually pays right now, as a decimal (0.05). */
  rate: z.number(),
  baseRate: z.number(),
  isDiscounted: z.boolean(),
  /** Pre-formatted for display so every surface renders it identically. */
  rateLabel: z.string(),
  baseRateLabel: z.string(),
  ruleName: z.string().nullable(),
  endDate: z.coerce.date().nullable(),
  /**
   * The closest rule the seller has NOT yet earned, with their progress toward
   * it. Null when no rule is active or they already hold the best one.
   */
  nextTier: z.object({
    ruleName: z.string(),
    rate: z.number(),
    rateLabel: z.string(),
    progress: z.array(SellerFeeProgressSchema),
  }).nullable(),
});

export type SellerFeeStatus = z.infer<typeof SellerFeeStatusSchema>;

export const SellerFeeStatusResponseSchema = z.object({
  success: z.literal(true),
  feeStatus: SellerFeeStatusSchema,
});

export type SellerFeeStatusResponse = z.infer<typeof SellerFeeStatusResponseSchema>;
